import { useMemo } from 'react';

import { Button, TableColumn } from '@app/components';
import { ConfirmDialog } from '@app/hoc/confirm-dialog/ConfirmDialog';

import { Typography } from '@mui/material';
import { QrCodeRiskLogGroupedOutDto } from '@app/swagger-types';
import { NestedKeyOf } from '@app/utils/type.utils';
import { format } from 'date-fns';
import { FORMAT } from '@app/constants/formats';
import { clsxm } from '@app/styles/clsxm';

const getEntityField = (v: NestedKeyOf<QrCodeRiskLogGroupedOutDto>) => v;

interface Args {
  onViewHistory: (qrCodeId: string) => void;
  onToggleReviewed: (qrCodeId: string, reviewed: boolean) => void;
  togglingQrCodeId?: string;
  onBlock: (qrCodeId: string) => void;
  blockingQrCodeId?: string;
  onUnblock: (qrCodeId: string) => void;
  unblockingQrCodeId?: string;
}

export const useGroupedTableColumns = ({
  onViewHistory,
  onToggleReviewed,
  togglingQrCodeId,
  onBlock,
  blockingQrCodeId,
  onUnblock,
  unblockingQrCodeId,
}: Args) => {
  const tableColumns = useMemo<TableColumn<QrCodeRiskLogGroupedOutDto>[]>(() => {
    return [
      {
        headerName: 'QR code',
        field: getEntityField('qrCodeId'),
        align: 'left',
        disableSort: true,
        render: ({ qrCodeId }) => (
          <Typography variant="xs" noWrap>
            {qrCodeId ?? '-'}
          </Typography>
        ),
      },
      {
        headerName: 'Last source',
        field: getEntityField('lastSourceAction'),
        align: 'left',
        disableSort: true,
        render: ({ lastSourceAction, accountCategory }) => (
          <div className="flex flex-col items-start gap-1">
            <Typography variant="xs">{lastSourceAction}</Typography>
            <span className="text-xxs text-gray-500">{accountCategory}</span>
          </div>
        ),
      },
      {
        headerName: 'Last destination',
        field: getEntityField('lastFinalUrl'),
        align: 'left',
        disableSort: true,
        render: ({ lastFinalUrl }) => (
          <Typography variant="xs" className="truncate max-w-[320px] block">
            {lastFinalUrl ?? '-'}
          </Typography>
        ),
      },
      {
        headerName: 'Web Risk',
        field: getEntityField('lastWebRiskVerdict'),
        align: 'left',
        disableSort: true,
        render: ({ lastWebRiskVerdict }) => <Typography variant="xs">{lastWebRiskVerdict ?? '-'}</Typography>,
      },
      {
        headerName: 'Would block',
        field: getEntityField('wouldBlock'),
        align: 'center',
        disableSort: true,
        render: ({ wouldBlock }) => (
          <span className={clsxm('text-xs rounded px-1 text-white', wouldBlock ? 'bg-red-600' : 'bg-emerald-600')}>
            {wouldBlock ? 'would block' : 'clean'}
          </span>
        ),
      },
      {
        headerName: 'Blocked',
        field: getEntityField('riskBlocked'),
        align: 'center',
        disableSort: true,
        render: ({ riskBlocked }) =>
          riskBlocked ? (
            <span className="text-xs rounded px-1 text-white bg-red-800">blocked</span>
          ) : (
            <Typography variant="xs" className="text-gray-400">
              -
            </Typography>
          ),
      },
      {
        headerName: 'Flags',
        field: getEntityField('flagCount'),
        align: 'center',
        disableSort: true,
        render: ({ flagCount, firstFlaggedDate, lastFlaggedDate }) => (
          <div className="flex flex-col items-center gap-1">
            <Typography variant="xs">{flagCount}×</Typography>
            <span className="text-xxs text-gray-500" title="First → last flagged">
              {firstFlaggedDate ? format(new Date(firstFlaggedDate), FORMAT.FULL_TIME) : '-'}
              {' → '}
              {lastFlaggedDate ? format(new Date(lastFlaggedDate), FORMAT.FULL_TIME) : '-'}
            </span>
          </div>
        ),
      },
      {
        headerName: 'Status',
        field: getEntityField('reviewed'),
        align: 'center',
        disableSort: true,
        render: ({ reviewed }) => (
          <span className={clsxm('text-xs rounded px-1 text-white', reviewed ? 'bg-slate-500' : 'bg-amber-600')}>
            {reviewed ? 'reviewed' : 'open'}
          </span>
        ),
      },
      {
        headerName: 'Actions',
        field: 'actions',
        disableSort: true,
        align: 'right',
        render: ({ qrCodeId, reviewed, riskBlocked }) => (
          <div className="flex items-center justify-end gap-2">
            {riskBlocked ? (
              <Button
                size="small"
                color="error"
                loading={unblockingQrCodeId === qrCodeId}
                onClick={() => qrCodeId && onUnblock(qrCodeId)}
              >
                Unblock
              </Button>
            ) : (
              <ConfirmDialog
                title="Block this QR code?"
                text="The QR code stops redirecting immediately and the owner gets notified by email. This is reversible from this same table."
                confirmText="Block"
                confirmButtonProps={{ color: 'error' }}
                onConfirm={() => qrCodeId && onBlock(qrCodeId)}
                trigger={
                  <Button size="small" color="error" variant="outlined" loading={blockingQrCodeId === qrCodeId}>
                    Block
                  </Button>
                }
              />
            )}
            <Button
              size="small"
              loading={togglingQrCodeId === qrCodeId}
              onClick={() => qrCodeId && onToggleReviewed(qrCodeId, !reviewed)}
            >
              {reviewed ? 'Mark unreviewed' : 'Mark reviewed'}
            </Button>
            <Button size="small" variant="outlined" onClick={() => qrCodeId && onViewHistory(qrCodeId)}>
              History
            </Button>
          </div>
        ),
      },
    ];
  }, [onViewHistory, onToggleReviewed, togglingQrCodeId, onBlock, blockingQrCodeId, onUnblock, unblockingQrCodeId]);

  return tableColumns;
};
